import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import type { ChangelogChangelogReadModel } from '@r/api/generated/client.schemas.ts';
import { RichText } from '@r/components/rich-text.tsx';
import { cn } from '@r/utils/utils.ts';
import { Badge } from '@ui/badge.tsx';
import { Card } from '@ui/card.tsx';
import { MediaViewer } from '@ui/media-viewer.tsx';
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator
} from '@ui/timeline.tsx';

import { CHANGELOG_TAG_BADGE_CLASS } from './changelog-tag.ts';

interface ChangelogTimelineItemProps {
  changelog: ChangelogChangelogReadModel;
  /** Affiche la date à gauche uniquement pour la première entrée d’un même jour. */
  showDate: boolean;
  isLast: boolean;
}

/**
 * Une entrée du flux Nouveautés : date + marqueur de frise à gauche, carte à droite.
 * Carte inspirée des cartes produit du front client : texte à gauche, média à droite.
 */
export function ChangelogTimelineItem({ changelog, showDate, isLast }: ChangelogTimelineItemProps) {
  const { t } = useTranslation();
  const date = changelog.createdAt ? new Date(changelog.createdAt) : null;
  const mediaUri = changelog.media?.pathUri ?? null;

  const formattedDate = date ? format(date, 'd MMM yyyy', { locale: fr }) : null;

  return (
    <>
      {/* Mobile : la colonne date fixe (w-24) gâche trop d'espace, on l'intercale
          en en-tête au-dessus du groupe de cards du jour à la place. */}
      {showDate && formattedDate && (
        <p className="mb-2 mt-2 text-sm font-medium sm:hidden">{formattedDate}</p>
      )}

      <TimelineItem>
        <TimelineOppositeContent className="hidden w-24 sm:block">
          {showDate && formattedDate && <p className="font-medium">{formattedDate}</p>}
        </TimelineOppositeContent>

        <TimelineSeparator>
          <TimelineDot />
          {!isLast && <TimelineConnector />}
        </TimelineSeparator>

        <TimelineContent>
          <Card className="overflow-hidden border-0 bg-background">
            <div className={cn(mediaUri && 'sm:grid sm:grid-cols-12')}>
              <div className={cn('min-w-0 space-y-2 p-6', mediaUri && 'sm:col-span-8')}>
                <Badge
                  className={cn(
                    CHANGELOG_TAG_BADGE_CLASS[changelog.tag],
                    'text-[10px] uppercase tracking-wide'
                  )}
                >
                  {t(`changelog.tag.${changelog.tag}`)}
                </Badge>

                <h3 className="text-lg font-semibold leading-snug">{changelog.title}</h3>
                <RichText html={changelog.description} className="text-muted-foreground" />
              </div>

              {mediaUri && (
                <div className="relative flex items-center justify-center p-8 sm:col-span-4 sm:p-6">
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-muted-foreground/10 to-transparent to-50% sm:bg-gradient-to-r" />
                  <MediaViewer
                    media={[mediaUri]}
                    alt={changelog.title}
                    className="relative mx-auto max-h-36 w-auto max-w-[70%] rounded-lg object-contain sm:max-h-52 sm:w-full sm:max-w-none"
                  />
                </div>
              )}
            </div>
          </Card>
        </TimelineContent>
      </TimelineItem>
    </>
  );
}
